import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  Plus, Search, Mail, Phone, Globe, Smartphone,
  Archive, Trash2, ArchiveRestore, RefreshCw, Eye, EyeOff,
  Contact, ChevronUp, ChevronDown, Users as UsersIcon
} from 'lucide-react';
import UserMultiSelect from './UserMultiSelect';

const VISIBILITY_LABEL = {
  owner: 'Privat',
  shared: 'Geteilt',
  public: 'Alle',
};

const Kontakte = () => {
  const { user } = useContext(AuthContext);
  const [kontaktArt, setKontaktArt] = useState('extern');
  const [contacts, setContacts] = useState([]);
  const [fields, setFields] = useState([]); // fields config for current kontaktArt
  const [dropdowns, setDropdowns] = useState({}); // {key: [values]}
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [sortKey, setSortKey] = useState('updated_at');
  const [sortDir, setSortDir] = useState('desc');

  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null); // contact being edited
  const [formData, setFormData] = useState({});
  const [visibility, setVisibility] = useState('owner');
  const [sharedUserIds, setSharedUserIds] = useState([]);
  const [sharedGroupIds, setSharedGroupIds] = useState([]);
  const [sharedDeptIds, setSharedDeptIds] = useState([]);
  const [plzLookupLoading, setPlzLookupLoading] = useState(false);
  const [plzCandidates, setPlzCandidates] = useState([]);

  const isAdmin = user?.role === 'admin'
    || user?.accessible_features?.some(f => f.id === 'kontaktverwaltung' && f.permission_level === 'admin');
  const canEdit = isAdmin
    || user?.accessible_features?.some(f => f.id === 'kontaktverwaltung' && (f.permission_level === 'edit' || f.permission_level === 'admin'));

  const loadCommon = useCallback(async () => {
    const [ddRes, grpRes, usrRes, deptRes] = await Promise.all([
      axios.get('/kontakte/dropdowns'),
      axios.get('/kontakte/groups'),
      axios.get('/kontakte/_/users'),
      axios.get('/kontakte/_/departments'),
    ]);
    const ddMap = {};
    (ddRes.data || []).forEach(d => { ddMap[d.key] = d.values; });
    setDropdowns(ddMap);
    setGroups(grpRes.data || []);
    setUsers(usrRes.data || []);
    setDepartments(deptRes.data || []);
  }, []);

  const loadFields = useCallback(async (art) => {
    const r = await axios.get(`/kontakte/fields/${art}`);
    const sorted = (r.data.fields || []).slice().sort((a, b) => (a.sort_order ?? 99) - (b.sort_order ?? 99));
    setFields(sorted);
  }, []);

  const loadContacts = useCallback(async () => {
    const params = new URLSearchParams();
    params.set('kontakt_art', kontaktArt);
    if (search) params.set('search', search);
    if (includeArchived) params.set('include_archived', 'true');
    const r = await axios.get(`/kontakte?${params.toString()}`);
    setContacts(r.data || []);
  }, [kontaktArt, search, includeArchived]);

  useEffect(() => {
    let m = true;
    (async () => {
      setLoading(true);
      try {
        await loadCommon();
      } catch (err) {
        toast.error('Fehler beim Laden');
      } finally {
        if (m) setLoading(false);
      }
    })();
    return () => { m = false; };
  }, [loadCommon]);

  useEffect(() => {
    (async () => {
      try { await loadFields(kontaktArt); } catch {}
      try { await loadContacts(); } catch {}
    })();
  }, [kontaktArt, loadFields, loadContacts]);

  const tableFields = useMemo(() => fields.filter(f => f.in_table), [fields]);

  const sortedContacts = useMemo(() => {
    const arr = contacts.slice();
    arr.sort((a, b) => {
      let av, bv;
      if (sortKey === 'updated_at') { av = a.updated_at; bv = b.updated_at; }
      else { av = (a.form_data || {})[sortKey]; bv = (b.form_data || {})[sortKey]; }
      av = av ?? ''; bv = bv ?? '';
      if (Array.isArray(av)) av = av.join(', ');
      if (Array.isArray(bv)) bv = bv.join(', ');
      const cmp = String(av).toLowerCase().localeCompare(String(bv).toLowerCase());
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [contacts, sortKey, sortDir]);

  const openCreate = () => {
    setEditing(null);
    setFormData({});
    // Apply default values
    const initial = {};
    fields.forEach(f => {
      if (f.default_value !== undefined && f.default_value !== null) initial[f.key] = f.default_value;
    });
    setFormData(initial);
    setVisibility('owner');
    setSharedUserIds([]);
    setSharedGroupIds([]);
    setSharedDeptIds([]);
    setPlzCandidates([]);
    setShowDialog(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setFormData(c.form_data || {});
    setVisibility(c.visibility || 'owner');
    setSharedUserIds(c.shared_user_ids || []);
    setSharedGroupIds(c.shared_group_ids || []);
    setSharedDeptIds(c.shared_department_ids || []);
    setPlzCandidates([]);
    setShowDialog(true);
  };

  const saveContact = async () => {
    // Validate required
    for (const f of fields) {
      if (f.required && !formData[f.key]) {
        toast.error(`Pflichtfeld fehlt: ${f.label}`);
        return;
      }
    }
    try {
      const isOwner = !editing || editing.owner_id === user?.id;
      const canChangeVisibility = isAdmin || isOwner;

      const payload = {
        kontakt_art: kontaktArt,
        form_data: formData,
      };
      if (canChangeVisibility) {
        payload.visibility = visibility;
        payload.shared_user_ids = sharedUserIds;
        payload.shared_group_ids = sharedGroupIds;
        payload.shared_department_ids = sharedDeptIds;
      }
      if (editing) {
        await axios.patch(`/kontakte/${editing.id}`, payload);
        toast.success('Kontakt aktualisiert');
      } else {
        // Always send visibility on create (the new contact's owner is the current user)
        payload.visibility = visibility;
        payload.shared_user_ids = sharedUserIds;
        payload.shared_group_ids = sharedGroupIds;
        payload.shared_department_ids = sharedDeptIds;
        await axios.post('/kontakte', payload);
        toast.success('Kontakt erstellt');
      }
      setShowDialog(false);
      await loadContacts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const archiveContact = async (c) => {
    if (!window.confirm('Kontakt archivieren?')) return;
    try {
      await axios.post(`/kontakte/${c.id}/archive`);
      toast.success('Archiviert');
      await loadContacts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  const unarchiveContact = async (c) => {
    try {
      await axios.post(`/kontakte/${c.id}/unarchive`);
      toast.success('Wiederhergestellt');
      await loadContacts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  const deleteContact = async (c) => {
    if (!window.confirm('Kontakt endgültig löschen? (Nicht umkehrbar)')) return;
    try {
      await axios.delete(`/kontakte/${c.id}`);
      toast.success('Endgültig gelöscht');
      await loadContacts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  const lookupPlz = async (plz) => {
    setPlzLookupLoading(true);
    setPlzCandidates([]);
    try {
      const country = (formData.land || 'Deutschland').toLowerCase().startsWith('deutsch') ? 'DE' : null;
      if (!country) { toast.info('PLZ-Lookup nur für Deutschland'); setPlzLookupLoading(false); return; }
      const r = await axios.get(`/kontakte/_/plz-lookup?plz=${encodeURIComponent(plz)}&country=DE`);
      const places = r.data.places || [];
      if (places.length === 0) { toast.error('Keine Orte gefunden'); }
      else if (places.length === 1) {
        setFormData(p => ({ ...p, ort: places[0].ort }));
        toast.success(`Ort: ${places[0].ort}`);
      } else {
        setPlzCandidates(places);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'PLZ-Lookup fehlgeschlagen'); }
    finally { setPlzLookupLoading(false); }
  };

  const renderFieldInput = (f) => {
    const v = formData[f.key];
    const setV = (val) => setFormData(p => ({ ...p, [f.key]: val }));
    const isOrtReadonly = f.key === 'ort' && !isAdmin && editing;

    switch (f.type) {
      case 'short_text':
      case 'phone':
        return <Input value={v || ''} onChange={e => setV(e.target.value)} placeholder={f.placeholder} readOnly={f.readonly || isOrtReadonly} data-testid={`field-${f.key}`} />;
      case 'long_text':
        return <Textarea value={v || ''} onChange={e => setV(e.target.value)} placeholder={f.placeholder} rows={3} data-testid={`field-${f.key}`} />;
      case 'number':
        return <Input type="number" value={v ?? ''} onChange={e => setV(e.target.value)} data-testid={`field-${f.key}`} />;
      case 'email':
        return <Input type="email" value={v || ''} onChange={e => setV(e.target.value)} placeholder={f.placeholder || 'name@beispiel.de'} data-testid={`field-${f.key}`} />;
      case 'url':
        return <Input type="url" value={v || ''} onChange={e => setV(e.target.value)} placeholder="https://..." data-testid={`field-${f.key}`} />;
      case 'date':
        return <Input type="date" value={v || ''} onChange={e => setV(e.target.value)} data-testid={`field-${f.key}`} />;
      case 'date_optional_year':
        // Allow either "2026-05-26" or "05-26" (no year)
        return (
          <div className="flex gap-2 items-center">
            <Input type="text" value={v || ''} onChange={e => setV(e.target.value)} placeholder="TT.MM oder TT.MM.JJJJ" data-testid={`field-${f.key}`} />
            <span className="text-xs text-gray-400">Jahr optional</span>
          </div>
        );
      case 'yes_no':
        return <div className="flex items-center gap-2"><Switch checked={!!v} onCheckedChange={setV} data-testid={`field-${f.key}`} /><span className="text-sm">{v ? 'Ja' : 'Nein'}</span></div>;
      case 'checkbox':
        return <input type="checkbox" checked={!!v} onChange={e => setV(e.target.checked)} data-testid={`field-${f.key}`} className="rounded" />;
      case 'dropdown':
      case 'country_dropdown': {
        const opts = f.type === 'country_dropdown' ? (dropdowns.laender || []) : (dropdowns[f.dropdown_key] || []);
        return (
          <Select value={v || '_none'} onValueChange={val => setV(val === '_none' ? '' : val)}>
            <SelectTrigger data-testid={`field-${f.key}`}><SelectValue placeholder={f.placeholder || 'Auswählen...'} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">—</SelectItem>
              {opts.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        );
      }
      case 'multiselect': {
        const opts = dropdowns[f.dropdown_key] || [];
        const arr = Array.isArray(v) ? v : [];
        return (
          <div className="flex flex-wrap gap-1.5" data-testid={`field-${f.key}`}>
            {opts.map(o => {
              const active = arr.includes(o);
              return (
                <button key={o} type="button"
                  onClick={() => setV(active ? arr.filter(x => x !== o) : [...arr, o])}
                  className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${active ? 'bg-[#e30613] text-white border-[#e30613]' : 'bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-700'}`}>
                  {o}
                </button>
              );
            })}
            {opts.length === 0 && <span className="text-xs text-gray-400 italic">Keine Optionen konfiguriert</span>}
          </div>
        );
      }
      case 'user_select':
        return (
          <Select value={v || '_none'} onValueChange={val => setV(val === '_none' ? '' : val)}>
            <SelectTrigger data-testid={`field-${f.key}`}><SelectValue placeholder="Person wählen..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">—</SelectItem>
              {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
            </SelectContent>
          </Select>
        );
      case 'address':
        return <Textarea value={v || ''} onChange={e => setV(e.target.value)} rows={2} placeholder="Straße, PLZ, Ort" data-testid={`field-${f.key}`} />;
      default:
        return <Input value={v || ''} onChange={e => setV(e.target.value)} data-testid={`field-${f.key}`} />;
    }
  };

  const renderTableCell = (c, f) => {
    const v = (c.form_data || {})[f.key];
    if (v === undefined || v === null || v === '') return <span className="text-gray-300">—</span>;
    if (Array.isArray(v)) return <div className="flex flex-wrap gap-0.5">{v.map(x => <Badge key={x} variant="outline" className="text-[10px]">{x}</Badge>)}</div>;
    if (f.type === 'email') return <a href={`mailto:${v}`} className="text-blue-600 hover:underline" onClick={e => e.stopPropagation()}>{v}</a>;
    if (f.type === 'phone') return <a href={`tel:${v}`} className="text-blue-600 hover:underline" onClick={e => e.stopPropagation()}>{v}</a>;
    if (f.type === 'url') return <a href={v.startsWith('http') ? v : `https://${v}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline" onClick={e => e.stopPropagation()}>{v}</a>;
    return <span className="truncate block max-w-[200px]">{String(v)}</span>;
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20"><RefreshCw className="w-5 h-5 animate-spin text-[#e30613] mr-2" /><span>Lade...</span></div>
  );

  return (
    <div className="space-y-4" data-testid="kontakte-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Contact className="w-6 h-6 text-[#e30613]" /> Kontakte</h1>
          <p className="text-sm text-gray-500 mt-1">Externe und interne Kontakte zentral verwalten</p>
        </div>
        {canEdit && (
          <Button onClick={openCreate} className="bg-[#e30613] hover:bg-[#c00510]" data-testid="new-contact-btn">
            <Plus className="w-4 h-4 mr-1" /> Neuer Kontakt
          </Button>
        )}
      </div>

      <Tabs value={kontaktArt} onValueChange={setKontaktArt}>
        <TabsList>
          <TabsTrigger value="extern" data-testid="tab-extern">Externe Kontakte</TabsTrigger>
          <TabsTrigger value="intern" data-testid="tab-intern">Interne Kontakte</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input className="pl-8 h-9 text-sm" placeholder="Volltextsuche..." value={search} onChange={e => setSearch(e.target.value)} data-testid="search-input" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={includeArchived} onCheckedChange={setIncludeArchived} data-testid="archive-toggle" />
              {includeArchived ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              Archiv anzeigen
            </label>
            <Badge variant="secondary" className="ml-auto">{sortedContacts.length} Kontakte</Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {sortedContacts.length === 0 ? (
            <p className="text-center text-gray-500 py-10">Keine Kontakte. {canEdit && 'Erstelle den ersten Kontakt!'}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-800">
                    {tableFields.map(f => (
                      <th key={f.key} className="text-left px-2 py-2 font-semibold cursor-pointer select-none" onClick={() => toggleSort(f.key)}>
                        <div className="flex items-center gap-1">
                          {f.label}
                          {sortKey === f.key && (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                        </div>
                      </th>
                    ))}
                    <th className="text-left px-2 py-2 font-semibold">Sichtbarkeit</th>
                    <th className="text-right px-2 py-2 font-semibold">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedContacts.map(c => (
                    <tr key={c.id} className={`border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900 cursor-pointer ${c.is_archived ? 'opacity-60' : ''}`} onClick={() => openEdit(c)} data-testid={`contact-row-${c.id}`}>
                      {tableFields.map(f => (
                        <td key={f.key} className="px-2 py-2">{renderTableCell(c, f)}</td>
                      ))}
                      <td className="px-2 py-2">
                        <Badge variant="outline" className="text-[10px]">{VISIBILITY_LABEL[c.visibility] || c.visibility}</Badge>
                        {c.is_archived && <Badge variant="secondary" className="text-[10px] ml-1">Archiv</Badge>}
                      </td>
                      <td className="px-2 py-2 text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          {c.is_archived ? (
                            <>
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => unarchiveContact(c)} data-testid={`unarchive-${c.id}`} title="Wiederherstellen">
                                <ArchiveRestore className="w-3.5 h-3.5" />
                              </Button>
                              {isAdmin && (
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => deleteContact(c)} data-testid={`delete-${c.id}`} title="Endgültig löschen">
                                  <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                </Button>
                              )}
                            </>
                          ) : (
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => archiveContact(c)} data-testid={`archive-${c.id}`} title="Archivieren">
                              <Archive className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit/Create dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="contact-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Kontakt bearbeiten' : 'Neuer Kontakt'} ({kontaktArt === 'extern' ? 'Extern' : 'Intern'})</DialogTitle>
            <DialogDescription>
              {editing ? `Erstellt von ${editing.owner_name || '—'}` : 'Pflichtfelder sind mit * markiert'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            {fields.map(f => (
              <div key={f.id || f.key} className={f.type === 'long_text' || f.type === 'address' ? 'md:col-span-2' : ''}>
                <Label className="text-xs">
                  {f.label}
                  {f.required && <span className="text-red-500 ml-0.5">*</span>}
                </Label>
                {f.help_text && <p className="text-xs text-gray-400 mb-1">{f.help_text}</p>}
                {renderFieldInput(f)}
                {/* PLZ lookup button */}
                {f.key === 'plz' && (
                  <div className="flex items-center gap-2 mt-1">
                    <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!formData.plz || plzLookupLoading} onClick={() => lookupPlz(formData.plz)} data-testid="plz-lookup-btn">
                      {plzLookupLoading ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <Search className="w-3 h-3 mr-1" />}
                      Ort ermitteln
                    </Button>
                    {plzCandidates.length > 0 && (
                      <Select onValueChange={(val) => { setFormData(p => ({ ...p, ort: val })); setPlzCandidates([]); }}>
                        <SelectTrigger className="h-7 text-xs w-48"><SelectValue placeholder={`${plzCandidates.length} Orte`} /></SelectTrigger>
                        <SelectContent>
                          {plzCandidates.map((p, i) => <SelectItem key={i} value={p.ort}>{p.ort} ({p.bundesland_kuerzel})</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Visibility section — only owner or admin may change */}
          {(() => {
            const isOwner = !editing || editing.owner_id === user?.id;
            const canChangeVisibility = isAdmin || isOwner;
            if (!canChangeVisibility) {
              // Show read-only info badge
              return (
                <div className="mt-4 pt-4 border-t">
                  <h3 className="font-semibold text-sm flex items-center gap-2 mb-2"><UsersIcon className="w-4 h-4" /> Sichtbarkeit</h3>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Badge variant="outline">{VISIBILITY_LABEL[visibility] || visibility}</Badge>
                    <span className="text-xs italic">Nur der Ersteller oder ein Admin kann die Sichtbarkeit ändern.</span>
                  </div>
                </div>
              );
            }
            return (
              <div className="mt-4 pt-4 border-t space-y-3">
                <h3 className="font-semibold text-sm flex items-center gap-2"><UsersIcon className="w-4 h-4" /> Sichtbarkeit</h3>
                <Select value={visibility} onValueChange={setVisibility}>
                  <SelectTrigger data-testid="visibility-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owner">Privat — nur ich</SelectItem>
                    <SelectItem value="shared">Geteilt — bestimmte User / Gruppen / Abteilungen</SelectItem>
                    {isAdmin && <SelectItem value="public">Alle — global sichtbar</SelectItem>}
                  </SelectContent>
                </Select>

                {visibility === 'shared' && (
                  <div className="space-y-3 pl-2 border-l-2 border-gray-200 dark:border-gray-700">
                    <div>
                      <Label className="text-xs">User freigeben</Label>
                      <UserMultiSelect users={users} selectedIds={sharedUserIds} onChange={setSharedUserIds} testId="share-users" placeholder="Nach Name suchen…" />
                    </div>
                    <div>
                      <Label className="text-xs">Kontaktgruppen</Label>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {groups.length === 0 ? <span className="text-xs text-gray-400">Keine Gruppen vorhanden</span> : groups.map(g => {
                          const active = sharedGroupIds.includes(g.id);
                          return (
                            <button key={g.id} type="button"
                              onClick={() => setSharedGroupIds(active ? sharedGroupIds.filter(x => x !== g.id) : [...sharedGroupIds, g.id])}
                              className={`px-2 py-0.5 rounded-full text-xs border ${active ? 'bg-[#e30613] text-white border-[#e30613]' : 'bg-gray-50 dark:bg-gray-800 border-gray-300'}`}
                              data-testid={`share-group-${g.id}`}>
                              {g.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Abteilungen</Label>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {departments.length === 0 ? <span className="text-xs text-gray-400">Keine Abteilungen</span> : departments.map(d => {
                          const active = sharedDeptIds.includes(d.id);
                          return (
                            <button key={d.id} type="button"
                              onClick={() => setSharedDeptIds(active ? sharedDeptIds.filter(x => x !== d.id) : [...sharedDeptIds, d.id])}
                              className={`px-2 py-0.5 rounded-full text-xs border ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-50 dark:bg-gray-800 border-gray-300'}`}>
                              {d.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          <div className="flex justify-end gap-2 pt-4 border-t mt-4">
            <Button variant="outline" onClick={() => setShowDialog(false)}>Abbrechen</Button>
            <Button onClick={saveContact} className="bg-[#e30613] hover:bg-[#c00510]" data-testid="save-contact-btn">
              {editing ? 'Speichern' : 'Erstellen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Kontakte;
